import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  useNavigate,
  useLocation,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import supabase from "./config/supabaseConfig";
import Login from "./components/Login";
import LoadingScreen from "./components/LoadingScreen";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
// Componentes
import Sala from "./components/Sala";
import Configuracoes from "./components/Configuracoes";
import GerenciamentoAlunos from "./components/GerenciamentoAlunos";
import CadastroAlunoNovo from "./components/CadastroAlunoNovo";
import GerenciamentoProfessores from "./components/GerenciamentoProfessores";
import DetalheCadastroAluno from "./components/DetalheCadastroAluno";
import EditarAluno from "./components/EditarAluno";
import AlunoFullScreen from "./components/AlunoFullScreen";
import Telao from "./components/Telao";
import Dashboard from "./components/Dashboard";
// Moldura da UI nova (patch 2026) — showcase /novo/* foi removido no B4
import NovaShell from "./components/nova-ui/NovaShell";
import RelatoriosNovo from "./components/RelatoriosNovo";
import PresalaNova from "./components/PresalaNova";
import CargasAluno from "./components/CargasAluno";
import { getViewAs, descartarViewAs } from "./lib/viewAs";
import { SalaProvider } from "./contexts/SalaContext";
import { CadastroAlunoProvider } from "./contexts/CadastroAlunoContext";
import startAutoEndScheduler from "./services/scheduler";
import "./App.css";

// Chaves do localStorage
const CADASTRO_FORM_STORAGE_KEY = "ironhouse_cadastroAlunoForm";
const AULA_ATUAL_STORAGE_KEY = "ironhouse_aulaAtual";

// Componente wrapper para DetalheCadastroAluno
const DetalheAlunoWrapper = ({ onNavigateBack }) => {
  const { id } = useParams();
  return <DetalheCadastroAluno alunoId={id} onNavigateBack={onNavigateBack} />;
};

const App = () => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Sempre inicia como true (colapsado/escondido)
  const [alunosEmAula, setAlunosEmAula] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Modo coringa "Ver como" — só admin DA ALLOWLIST pode simular outro papel.
  // userRole segue sendo o papel REAL; effectiveRole é o que a UI/gates usam.
  // O getViewAs recebe o e-mail e só devolve o modo se ele tiver sido ligado
  // por esse mesmo usuário: antes bastava ser admin, e um modo esquecido na
  // aba (ou ligado por outra conta) prendia o usuário na visão de professor.
  const viewAs = userRole === "admin" ? getViewAs(user?.email) : null;
  const effectiveRole = viewAs?.role || userRole;
  // Rotas da UI nova (patch 2026) renderizam em tela cheia, sem a moldura
  // (Header/Sidebar) do app antigo.
  const isNovaUI =
    location.pathname.startsWith("/aluno") ||
    location.pathname.startsWith("/sala") || // migrada: usa a moldura nova
    location.pathname.startsWith("/alunos") || // migrada
    location.pathname.startsWith("/professores") || // migrada
    location.pathname.startsWith("/detalhe-aluno") || // migrada
    location.pathname.startsWith("/cadastro-aluno") || // migrada
    location.pathname.startsWith("/editar-aluno") || // migrada
    location.pathname.startsWith("/relatorios") || // migrada
    location.pathname.startsWith("/presala") || // migrada
    location.pathname.startsWith("/cargas") || // migrada
    location.pathname.startsWith("/configuracoes") || // migrada
    location.pathname.startsWith("/telao") || // migrada: full-bleed, sem NovaShell
    location.pathname.startsWith("/dashboard") || // migrada
    location.pathname === "/login";

  // Efeito principal para autenticação e verificação de sessão
  useEffect(() => {
    let isMounted = true;

    // Função para inicializar a aplicação (simplificada)
    const initializeApp = async () => {
      try {
        // 1. Obter sessão atual
        const { data: sessionData, error: sessionError } =
          await supabase.auth.getSession();

        if (sessionError) {
          console.error("[App.jsx] Erro ao obter sessão:", sessionError);
          if (isMounted) {
            setUser(null);
            setUserRole(null);
            navigate("/login");
            setLoading(false);
          }
          return;
        }

        const currentUser = sessionData?.session?.user;

        // 2. Se não tem usuário logado, mostrar login
        if (!currentUser) {
          if (isMounted) {
            setUser(null);
            setUserRole(null);
            navigate("/login");
            setLoading(false);
          }
          return;
        }

        // 3. Usuário logado, definir usuário e buscar role
        if (isMounted) {
          setUser(currentUser);
        }

        // 4. Buscar role do usuário
        let userRoleValue = null;
        try {
          const { data: profile } = await supabase
            .from("profiles")
            .select("role")
            .eq("id", currentUser.id)
            .single();

          if (profile) {
            userRoleValue = profile.role;
          }
        } catch (error) {
          console.error("[App.jsx] Erro ao buscar role:", error);
        }

        // 5. IMPORTANTE: Atualizar estados MESMO QUE HAJA ERROS nas etapas anteriores
        if (isMounted) {
          setUserRole(userRoleValue);
          setLoading(false);
        }
      } catch (error) {
        console.error("[App.jsx] Erro GERAL em initializeApp:", error);
        // Mesmo com erro, definir tela de login e sair do loading
        if (isMounted) {
          setUser(null);
          setUserRole(null);
          navigate("/login");
          setLoading(false);
        }
      }
    };

    // Chamar inicialização
    initializeApp();

    // Configurar listener de autenticação simplificado
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!isMounted) return;

        const currentUser = session?.user;
        setUser(currentUser);

        if (!currentUser) {
          // Se não tem usuário (logout), ir para login
          setUserRole(null);
          navigate("/login");
          setLoading(false);
        } else if (_event === "SIGNED_IN") {
          // Se acabou de logar, buscar role
          supabase
            .from("profiles")
            .select("role")
            .eq("id", currentUser.id)
            .single()
            .then(({ data }) => {
              if (isMounted) {
                const role = data?.role || null;
                setUserRole(role);
                setLoading(false);
              }
            })
            .catch((error) => {
              console.error("[App.jsx] Erro ao buscar role após login:", error);
              if (isMounted) {
                setUserRole(null);
                setLoading(false);
              }
            });
        }
      }
    );

    // Definir um fallback para garantir que loading sempre termine
    const timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        setLoading(false);
      }
    }, 5000); // 5 segundos

    // Cleanup
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []); // Sem dependências para evitar re-execuções

  // Efeito para o Scheduler (separado)
  useEffect(() => {
    let isMounted = true;
    let stopScheduler = null;

    // Iniciar apenas se não estivermos no estado de loading inicial
    // e se o componente ainda estiver montado
    if (!loading && isMounted) {
      stopScheduler = startAutoEndScheduler();
    }

    return () => {
      isMounted = false;
      if (stopScheduler) {
        stopScheduler();
      }
    };
  }, [loading]); // Depende apenas do estado de loading

  // Adicionar um efeito para lidar com eventos de navegação customizada
  useEffect(() => {
    const handleNavigation = (event) => {
      const { secao } = event.detail;

      if (secao.startsWith("detalhe-aluno/")) {
        const id = secao.split("/")[1];
        navigate(`/detalhe-aluno/${id}`);
      } else if (secao.startsWith("editar-aluno/")) {
        const id = secao.split("/")[1];
        navigate(`/editar-aluno/${id}`);
      } else {
        navigate(`/${secao}`);
      }
    };

    window.addEventListener("navegarPara", handleNavigation);

    return () => {
      window.removeEventListener("navegarPara", handleNavigation);
    };
  }, [navigate]);

  // Função para verificar se o usuário pode acessar uma seção
  // Usa effectiveRole (respeita o modo coringa "Ver como" do admin real).
  const canUserAccessSection = useCallback(
    (section) => {
      // Seção sala é acessível por padrão para usuários logados
      if (section === "sala" || section === "cadastros") return true;

      // Verificações adicionais baseadas em role
      if (!effectiveRole) return false; // Para outras seções, precisa de role
      if (effectiveRole === "admin") return true; // Admin acessa tudo
      if (effectiveRole === "professor") {
        // Professor só usa Sala + Alunos. Não acessa configuracoes,
        // relatórios, pré-sala (quem distribui os alunos pro professor,
        // não o contrário), professores (gestão de equipe, não é dele),
        // dashboard (visão gerencial) nem telão (tela da recepção).
        return (
          section !== "configuracoes" &&
          section !== "relatorios" &&
          section !== "presala" &&
          section !== "professores" &&
          section !== "dashboard" &&
          section !== "telao"
        );
      }
      if (effectiveRole === "lider") {
        // Líder: Pré-sala, Telão, Alunos (pedido Rodrigo 14/07) e
        // Professores (gestão de equipe junto do admin — desenho 03/07;
        // o menu já mostrava, mas a rota negava). As sub-rotas de aluno
        // entram juntas — o handleNavigate valida pelo 1º segmento da URL
        // (detalhe-aluno/editar-aluno/cadastro-aluno/cargas/aluno).
        return [
          "presala",
          "telao",
          "alunos",
          "professores",
          "detalhe-aluno",
          "editar-aluno",
          "cadastro-aluno",
          "cargas",
          "aluno",
        ].includes(section);
      }
      if (effectiveRole === "recepcao") {
        // Pré-sala (distribuição) + telão (exibição da recepção) e, a partir
        // de 22/07 (pedido Rodrigo), Alunos e Professores: a recepção cria/
        // edita alunos e SÓ edita professores (a RLS do banco já foi liberada
        // — este é o gate de front). As sub-rotas de aluno entram junto porque
        // o handleNavigate valida pelo 1º segmento da URL (detalhe-aluno/
        // editar-aluno/cadastro-aluno) — é o que faz o clique num aluno abrir
        // o detalhe. NÃO entram "cargas" (domínio do professor — decisão
        // explícita) nem "aluno" (full screen).
        return [
          "presala",
          "telao",
          "alunos",
          "professores",
          "detalhe-aluno",
          "editar-aluno",
          "cadastro-aluno",
        ].includes(section);
      }
      return false; // Por padrão, nega acesso
    },
    [effectiveRole]
  );

  // Função para lidar com o login
  const handleLogin = async (email, password) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      toast.success("Login realizado com sucesso!");
      navigate("/"); // HomeRedirect decide por papel (recepção → pré-sala)
    } catch (error) {
      console.error("Erro no login:", error);
      toast.error(`Falha no login: ${error.message}`);
    }
  };

  // Função para lidar com o logout
  const handleLogout = async () => {
    try {
      // Limpar dados dos relatórios antes do logout
      if (window.limparTodosDadosRelatorios) {
        window.limparTodosDadosRelatorios();
      }

      // O modo coringa morre com a sessão: sem isto ele sobrevivia ao logout e
      // valia pro PRÓXIMO usuário que entrasse na mesma aba (fix 27/07).
      descartarViewAs();

      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      // Limpeza de estados gerenciada pelo onAuthStateChange/useEffect
      toast.info("Você foi desconectado.");
    } catch (error) {
      console.error("Erro no logout:", error);
      toast.error(`Falha ao sair: ${error.message}`);
    }
  };

  // Função para atualizar os alunos em aula (passada para Sala)
  const atualizarAlunosEmAula = useCallback((alunos) => {
    setAlunosEmAula(alunos);
  }, []);

  // Função para controlar o estado do sidebar
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const newState = !prev;
      // Controlar o scroll do body quando o sidebar está aberto em mobile
      if (window.innerWidth <= 768) {
        if (newState) {
          // Fechando o sidebar
          document.body.style.overflow = "";
        } else {
          // Abrindo o sidebar
          document.body.style.overflow = "hidden";
        }
      }
      return newState;
    });
  }, []);

  // Função para mudar a rota com verificações
  const handleNavigate = useCallback(
    (path) => {
      // Extrair a seção base da rota
      const section = path.split("/")[1] || path;

      // 1. Verificar se está tentando sair de 'cadastros' com dados pendentes
      if (
        window.location.pathname.includes("/cadastros") &&
        !path.includes("/cadastros")
      ) {
        try {
          const savedForm = localStorage.getItem(CADASTRO_FORM_STORAGE_KEY);
          if (savedForm && savedForm !== "{}") {
            if (
              !window.confirm(
                "Você tem dados não salvos no cadastro de aluno. Se sair agora, poderá continuar depois. Deseja realmente sair?"
              )
            ) {
              return; // Não muda de seção se o usuário cancelar
            }
          }
        } catch (error) {
          console.warn(
            "Erro ao verificar formulário salvo ao sair da seção:",
            error
          );
        }
      }

      // 2. Verificar permissão de acesso
      if (!canUserAccessSection(section)) {
        console.warn(
          `Usuário ${userRole} não tem permissão para acessar ${section}.`
        );
        toast.warn("Você não tem permissão para acessar esta área.");
        return; // Impede a navegação
      }

      // 3. Navegar para a rota
      navigate(path);

      // 4. Fechar sidebar em mobile
      if (window.innerWidth <= 768) {
        setSidebarCollapsed(true);
        document.body.style.overflow = "";
      }
    },
    [navigate, userRole, canUserAccessSection]
  );

  // Rota inicial por papel: recepção cai na Pré-sala (não opera aula —
  // feedback Tales 07/07); demais papéis caem na Sala.
  const HomeRedirect = () => {
    if (loading) return <LoadingScreen />;
    if (!user) return <Navigate to="/login" replace />;
    // No SIGNED_IN o user chega na hora e o role chega DEPOIS (fetch async
    // do profile) — decidir sem o papel mandava recepção pra /sala. Segura
    // o redirect até o papel existir (bug pego pelo Rodrigo 15/07).
    if (!effectiveRole) return <LoadingScreen />;
    return (
      <Navigate
        to={effectiveRole === "recepcao" ? "/presala" : "/sala"}
        replace
      />
    );
  };

  // Componente para verificar autenticação e redirecionar, se necessário
  const ProtectedRoute = ({ children, requiredSection = null }) => {
    if (loading) {
      return <LoadingScreen />;
    }

    if (!user) {
      return <Navigate to="/login" replace />;
    }

    if (requiredSection && !canUserAccessSection(requiredSection)) {
      toast.warn("Você não tem permissão para acessar esta área.");
      // "/" = HomeRedirect decide por papel (o "/sala" fixo mandava a
      // recepção pra uma tela que ela não tem).
      return <Navigate to="/" replace />;
    }

    return children;
  };

  return (
    <CadastroAlunoProvider>
      <div className="app-container">
        {user && !loading && !isNovaUI && (
          <>
            <Header
              user={user}
              onLogout={handleLogout}
              toggleSidebar={toggleSidebar}
              sidebarCollapsed={sidebarCollapsed}
            />
            <Sidebar
              userRole={effectiveRole}
              navigate={handleNavigate}
              collapsed={sidebarCollapsed}
              toggleSidebar={toggleSidebar}
            />
          </>
        )}
        <main
          className={
            isNovaUI
              ? ""
              : `main-content ${
                  user && !loading && !sidebarCollapsed ? "" : "expanded"
                }`
          }
          style={isNovaUI ? { width: "100%", flex: "1 1 100%", margin: 0, padding: 0 } : undefined}
        >
          <div className={isNovaUI ? "" : "content-wrapper"}>
            <Routes>
              <Route
                path="/login"
                element={
                  loading ? (
                    <LoadingScreen />
                  ) : user ? (
                    // Já logado visitando /login: manda pro HomeRedirect ("/"),
                    // que decide por papel (recepção → pré-sala). O "/sala"
                    // fixo daqui perdia a corrida com o navigate("/") do
                    // handleLogin e jogava a recepção na Sala (bug go-live 21/07).
                    <Navigate to="/" replace />
                  ) : (
                    <Login onLogin={handleLogin} />
                  )
                }
              />

              <Route
                path="/sala"
                element={
                  <ProtectedRoute>
                    <NovaShell userRole={effectiveRole} realRole={userRole} user={user} onLogout={handleLogout}>
                      <SalaProvider>
                        <Sala
                          userRole={effectiveRole}
                          alunosEmAula={alunosEmAula}
                          atualizarAlunosEmAula={atualizarAlunosEmAula}
                        />
                      </SalaProvider>
                    </NovaShell>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/alunos"
                element={
                  <ProtectedRoute requiredSection="alunos">
                    <NovaShell userRole={effectiveRole} realRole={userRole} user={user} onLogout={handleLogout}>
                      <GerenciamentoAlunos navigate={handleNavigate} />
                    </NovaShell>
                  </ProtectedRoute>
                }
              />

              {/* Rotas órfãs /cadastros e /historico REMOVIDAS (B4): telas
                  legadas fora da moldura, substituídas por /cadastro-aluno e
                  pelo Histórico do detalhe — link velho cai no HomeRedirect. */}
              <Route
                path="/configuracoes"
                element={
                  <ProtectedRoute requiredSection="configuracoes">
                    <NovaShell
                      userRole={effectiveRole}
                      realRole={userRole}
                      user={user}
                      onLogout={handleLogout}
                    >
                      <Configuracoes />
                    </NovaShell>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/presala"
                element={
                  <ProtectedRoute requiredSection="presala">
                    <NovaShell
                      userRole={effectiveRole}
                      realRole={userRole}
                      user={user}
                      onLogout={handleLogout}
                    >
                      <PresalaNova />
                    </NovaShell>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/relatorios"
                element={
                  <ProtectedRoute requiredSection="relatorios">
                    <NovaShell
                      userRole={effectiveRole}
                      realRole={userRole}
                      user={user}
                      onLogout={handleLogout}
                    >
                      <RelatoriosNovo />
                    </NovaShell>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/aluno/:id"
                element={
                  <ProtectedRoute>
                    <AlunoFullScreen />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/telao"
                element={
                  <ProtectedRoute requiredSection="telao">
                    <Telao />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requiredSection="dashboard">
                    <NovaShell
                      userRole={effectiveRole}
                      realRole={userRole}
                      user={user}
                      onLogout={handleLogout}
                    >
                      <Dashboard userRole={effectiveRole} />
                    </NovaShell>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/detalhe-aluno/:id"
                element={
                  <ProtectedRoute>
                    <NovaShell
                      userRole={effectiveRole}
                      realRole={userRole}
                      user={user}
                      onLogout={handleLogout}
                    >
                      <DetalheAlunoWrapper onNavigateBack={handleNavigate} />
                    </NovaShell>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/editar-aluno/:id"
                element={
                  <ProtectedRoute requiredSection="alunos">
                    <NovaShell
                      userRole={effectiveRole}
                      realRole={userRole}
                      user={user}
                      onLogout={handleLogout}
                    >
                      <EditarAluno />
                    </NovaShell>
                  </ProtectedRoute>
                }
              />

              {/* Gate por "cargas" (não "alunos"): dar Alunos à recepção em
                  22/07 não pode vazar acesso a Cargas — domínio do professor
                  (decisão explícita). Admin/professor/líder seguem com cargas
                  via canUserAccessSection; recepção não. */}
              <Route
                path="/cargas/:id"
                element={
                  <ProtectedRoute requiredSection="cargas">
                    <NovaShell
                      userRole={effectiveRole}
                      realRole={userRole}
                      user={user}
                      onLogout={handleLogout}
                    >
                      <CargasAluno />
                    </NovaShell>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/professores"
                element={
                  <ProtectedRoute>
                    <NovaShell
                      userRole={effectiveRole}
                      realRole={userRole}
                      user={user}
                      onLogout={handleLogout}
                    >
                      <GerenciamentoProfessores userRole={effectiveRole} />
                    </NovaShell>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cadastro-aluno"
                element={
                  <ProtectedRoute>
                    <NovaShell
                      userRole={effectiveRole}
                      realRole={userRole}
                      user={user}
                      onLogout={handleLogout}
                    >
                      <CadastroAlunoNovo />
                    </NovaShell>
                  </ProtectedRoute>
                }
              />

              {/* Showcase /novo/* REMOVIDO (B4): serviu pro portão de
                  aprovação; as telas reais assumiram as rotas. Voltar do
                  browser caía nele e confundia (menu com emoji ≠ produto).
                  Link velho cai no HomeRedirect por papel. */}

              <Route path="/" element={<HomeRedirect />} />

              <Route path="*" element={<HomeRedirect />} />
            </Routes>
          </div>
        </main>
        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </CadastroAlunoProvider>
  );
};

export default App;
